import { useCallback, useMemo, useState } from 'react';
import { Typography, Link } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRowSelectionModel, GridRowId } from '@mui/x-data-grid';

import round from '@/util/round';
import SourceDocumentDialog from './SourceDocumentDialog';
import type { Doc } from '@/types/sharedTypes';
import type { EntityId } from '@/types/apiTypes';

interface SearchResultDoc extends Doc {
  id: EntityId;
  doc_id?: string;
  certainty?: number;
  start_page?: number;
  end_page?: number;
  page_number?: number;
  type?: string;
  [key: string]: unknown;
}

interface SearchResultsProps {
  docs: SearchResultDoc[];
  setSelectedDocument: (id: EntityId) => void;
}

function SearchResults({ docs, setSelectedDocument }: SearchResultsProps): React.JSX.Element {
  const [doc_open, setDocOpen] = useState<boolean>(false);
  const [active_view_doc, setActiveViewDoc] = useState<SearchResultDoc | null>(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  });

  const handleRowSelection = useCallback(
    (value: GridRowSelectionModel) => {
      setRowSelectionModel(value);
      const selectedIds = Array.from(value.ids);
      if (selectedIds[0]) {
        setSelectedDocument(selectedIds[0] as EntityId);
      }
    },
    [setSelectedDocument],
  );

  const handleModalClose = useCallback(() => {
    setDocOpen(false);
    setActiveViewDoc(null);
  }, []);

  const renderPage = useCallback(
    (params: { row: SearchResultDoc }) => {
      const doc = params.row;
      const doc_page =
        doc.start_page && doc.start_page !== doc.end_page
          ? `${doc.start_page} - ${doc.end_page}`
          : doc.start_page
          ? `${doc.start_page}`
          : doc.page_number
          ? `${doc.page_number}`
          : undefined;
      return <Typography variant='body2'>{doc_page}</Typography>;
    },
    [],
  );

  const columns: GridColDef<SearchResultDoc>[] = useMemo(
    () => [
      {
        field: 'certainty',
        headerName: 'Match %',
        type: 'number',
        width: 100,
        renderCell: (params) => {
          if (params.row?.certainty) {
            return `${round(params.row.certainty * 100, 4)}%`;
          }
          return '';
        },
      },
      {
        field: 'title',
        headerName: 'Document Title',
        type: 'string',
        flex: 1,
      },
      {
        field: 'page_number',
        headerName: 'Page Number',
        type: 'number',
        flex: 0.25,
        renderCell: renderPage,
      },
      {
        field: 'open',
        headerName: 'Open',
        renderCell: (params) => {
          return (
            <Link
              component='button'
              variant='body2'
              onClick={() => {
                setActiveViewDoc(params.row);
                setDocOpen(true);
              }}
            >
              <Typography>Open</Typography>
            </Link>
          );
        },
        flex: 0.25,
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'string',
        flex: 0.25,
      },
    ],
    [renderPage],
  );

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', height: '100%' }}>
        <div style={{ flexGrow: 1 }}>
          <DataGrid
            rowHeight={25}
            autoHeight
            getRowId={(row) => row.id}
            columns={columns}
            rows={docs || []}
            onRowSelectionModelChange={handleRowSelection}
            rowSelectionModel={rowSelectionModel}
          />
        </div>
      </div>
      {doc_open ? (
        <SourceDocumentDialog
          doc={active_view_doc}
          open={doc_open}
          handleClose={handleModalClose}
        />
      ) : undefined}
    </div>
  );
}

export default SearchResults;

